import type { NotificationAction, NotificationItem } from '@ignite/types';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Skeleton,
  ScrollArea,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@ignite/ui';
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Bell, Check, CircleAlert, CreditCard, FileText, ListTodo, Megaphone } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, readStoredUser } from '../../lib/auth';

type NotificationStatus = 'all' | 'unread';
type NotificationPage = Awaited<ReturnType<typeof apiClient.listNotifications>>;
type NotificationRoute =
  | '/'
  | '/account-data'
  | '/diagnosis'
  | '/competitor'
  | '/skills'
  | '/creator/drafts'
  | '/billing';

interface AnalyzerNotificationPresentation {
  sourceLabelKey: string;
  actionLabelKey: string;
  route: NotificationRoute;
  actionType: Extract<NotificationAction['type'], 'analysis_task' | 'workbench'>;
}

const analyzerNotificationPresentations = new Map<string, AnalyzerNotificationPresentation>([
  [
    'analyzer:self_note_analysis',
    {
      sourceLabelKey: 'portal.notifications.source.selfNoteAnalysis',
      actionLabelKey: 'portal.notifications.action.selfNoteAnalysis',
      route: '/account-data',
      actionType: 'analysis_task',
    },
  ],
  [
    'analyzer:competitor_insight',
    {
      sourceLabelKey: 'portal.notifications.source.competitorInsight',
      actionLabelKey: 'portal.notifications.action.competitorInsight',
      route: '/competitor',
      actionType: 'analysis_task',
    },
  ],
  [
    'analyzer:skill_extraction',
    {
      sourceLabelKey: 'portal.notifications.source.skillExtraction',
      actionLabelKey: 'portal.notifications.action.skillExtraction',
      route: '/skills',
      actionType: 'analysis_task',
    },
  ],
  [
    'analyzer:topic_recommend',
    {
      sourceLabelKey: 'portal.notifications.source.topicRecommend',
      actionLabelKey: 'portal.notifications.action.topicRecommend',
      route: '/competitor',
      actionType: 'analysis_task',
    },
  ],
  [
    'analyzer:daily_recommendation',
    {
      sourceLabelKey: 'portal.notifications.source.dailyRecommendation',
      actionLabelKey: 'portal.notifications.action.dailyRecommendation',
      route: '/',
      actionType: 'workbench',
    },
  ],
]);

function actionRoute(action: NotificationAction): NotificationRoute | null {
  switch (action.type) {
    case 'workbench':
      return '/';
    case 'analysis_task':
      return action.object_id ? '/diagnosis' : null;
    case 'content_draft':
      return action.object_id ? '/creator/drafts' : null;
    case 'billing':
      return action.object_id ? '/billing' : null;
  }
}

function notificationRoute(item: NotificationItem): NotificationRoute | null {
  const source = item.source_type
    ? analyzerNotificationPresentations.get(item.source_type)
    : undefined;
  if (source) {
    if (item.action?.type !== source.actionType) return null;
    if (item.action.type !== 'workbench' && !item.action.object_id) return null;
    return source.route;
  }
  if (item.source_type?.startsWith('analyzer:')) return null;
  return item.action ? actionRoute(item.action) : null;
}

function categoryIcon(category: NotificationItem['category']) {
  switch (category) {
    case 'task':
      return <ListTodo aria-hidden="true" />;
    case 'billing':
      return <CreditCard aria-hidden="true" />;
    case 'marketing':
      return <Megaphone aria-hidden="true" />;
    case 'system':
      return <CircleAlert aria-hidden="true" />;
  }
}

export function NotificationDrawer() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const storedUser = readStoredUser();
  const userScope = storedUser?.id ?? storedUser?.email ?? 'unknown';
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<NotificationStatus>('all');
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  const countKey = ['notifications', userScope, 'unread-count'] as const;
  const listKey = ['notifications', userScope, 'list', status] as const;

  const unreadQuery = useQuery({
    queryKey: countKey,
    queryFn: () => apiClient.notificationUnreadCount(),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  const notificationsQuery = useInfiniteQuery({
    queryKey: listKey,
    queryFn: ({ pageParam }) =>
      apiClient.listNotifications({ status, cursor: pageParam, limit: 30 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled: open,
  });

  const markRead = useMutation({
    mutationFn: (notificationId: string) => apiClient.markNotificationRead(notificationId),
    onSuccess: async (result, notificationId) => {
      const readAt = new Date().toISOString();
      queryClient.setQueryData<{ count: number }>(countKey, (current) =>
        current ? { count: Math.max(0, current.count - result.updated) } : current,
      );
      queryClient.setQueryData<InfiniteData<NotificationPage>>(
        ['notifications', userScope, 'list', 'all'],
        (current) => updateNotificationPages(current, notificationId, readAt, false),
      );
      queryClient.setQueryData<InfiniteData<NotificationPage>>(
        ['notifications', userScope, 'list', 'unread'],
        (current) => updateNotificationPages(current, notificationId, readAt, true),
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: countKey }),
        queryClient.invalidateQueries({ queryKey: ['notifications', userScope, 'list'] }),
      ]);
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => apiClient.markAllNotificationsRead(),
    onSuccess: async () => {
      setConfirmAllOpen(false);
      queryClient.setQueryData(countKey, { count: 0 });
      queryClient.setQueriesData<InfiniteData<NotificationPage>>(
        { queryKey: ['notifications', userScope, 'list', 'all'] },
        (current) =>
          current
            ? {
                ...current,
                pages: current.pages.map((page) => ({
                  ...page,
                  items: page.items.map((item) =>
                    item.read_at ? item : { ...item, read_at: new Date().toISOString() },
                  ),
                })),
              }
            : current,
      );
      queryClient.setQueryData<InfiniteData<NotificationPage>>(
        ['notifications', userScope, 'list', 'unread'],
        (current) =>
          current
            ? {
                ...current,
                pages: current.pages.map((page) => ({ ...page, items: [] })),
              }
            : current,
      );
      await queryClient.invalidateQueries({ queryKey: ['notifications', userScope] });
    },
  });

  const items = notificationsQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const unreadCount = unreadQuery.data?.count ?? 0;
  const mutationError = markRead.isError || markAllRead.isError;

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);
    if (nextOpen) void unreadQuery.refetch();
  }

  async function handleNotification(item: NotificationItem) {
    const route = notificationRoute(item);
    if (!route) return;
    if (!item.read_at) {
      try {
        await markRead.mutateAsync(item.id);
      } catch {
        return;
      }
    }
    setOpen(false);
    void navigate({ to: route });
  }

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetTrigger
          render={
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="notification-trigger"
              aria-label={t('portal.notifications.trigger', { count: unreadCount })}
              title={t('portal.action.notifications')}
            />
          }
        >
          <Bell aria-hidden="true" />
          {unreadCount > 0 ? (
            <Badge variant="destructive" className="notification-trigger__count" aria-hidden="true">
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          ) : null}
        </SheetTrigger>
        <SheetContent className="notification-drawer" closeLabel={t('portal.notifications.close')}>
          <SheetHeader className="notification-drawer__header">
            <SheetTitle>{t('portal.notifications.title')}</SheetTitle>
            <SheetDescription className="notification-drawer__summary">
              {t('portal.notifications.unreadSummary', { count: unreadCount })}
            </SheetDescription>
          </SheetHeader>

          <div className="notification-drawer__toolbar">
            <Tabs
              className="notification-drawer__tabs"
              value={status}
              onValueChange={(value) => setStatus(value as NotificationStatus)}
            >
              <TabsList>
                <TabsTrigger value="all">{t('portal.notifications.tab.all')}</TabsTrigger>
                <TabsTrigger value="unread">{t('portal.notifications.tab.unread')}</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button
              type="button"
              variant="outline"
              size="xs"
              disabled={unreadCount === 0 || markAllRead.isPending}
              onClick={() => setConfirmAllOpen(true)}
            >
              <Check aria-hidden="true" />
              {t('portal.notifications.markAllRead')}
            </Button>
          </div>

          {mutationError ? (
            <p className="notification-drawer__feedback" role="alert">
              {t('portal.notifications.state.actionError')}
            </p>
          ) : null}

          <ScrollArea className="notification-drawer__body">
            {notificationsQuery.isPending ? (
              <NotificationSkeleton />
            ) : notificationsQuery.isError ? (
              <NotificationState
                message={t('portal.notifications.state.loadError')}
                action={
                  <Button
                    type="button"
                    variant="outline"
                    size="xs"
                    onClick={() => notificationsQuery.refetch()}
                  >
                    {t('portal.notifications.retry')}
                  </Button>
                }
              />
            ) : items.length === 0 ? (
              <NotificationState message={t(`portal.notifications.state.${status}Empty`)} />
            ) : (
              <ul className="notification-list">
                {items.map((item) => {
                  const source = item.source_type
                    ? analyzerNotificationPresentations.get(item.source_type)
                    : undefined;
                  const route = notificationRoute(item);
                  return (
                    <li key={item.id} className="notification-item" data-unread={!item.read_at}>
                      <div className="notification-item__meta">
                        <span className="notification-item__source">
                          <span className="notification-item__icon">
                            {categoryIcon(item.category)}
                          </span>
                          <span>
                            {t(
                              source?.sourceLabelKey ??
                                `portal.notifications.category.${item.category}`,
                            )}
                          </span>
                        </span>
                        <time dateTime={item.created_at}>
                          {new Intl.DateTimeFormat(i18n.language, {
                            month: 'numeric',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          }).format(new Date(item.created_at))}
                        </time>
                      </div>
                      <p className="notification-item__title">{item.title}</p>
                      <p className="notification-item__body">{item.body}</p>
                      <div className="notification-item__actions">
                        {route ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="xs"
                            disabled={markRead.isPending}
                            onClick={() => void handleNotification(item)}
                          >
                            {t(source?.actionLabelKey ?? 'portal.notifications.open')}
                          </Button>
                        ) : !item.read_at ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            disabled={markRead.isPending}
                            onClick={() => markRead.mutate(item.id)}
                          >
                            {t('portal.notifications.markRead')}
                          </Button>
                        ) : null}
                        {item.action && !route ? (
                          <span className="notification-item__unavailable">
                            {t('portal.notifications.targetUnavailable')}
                          </span>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </ScrollArea>

          {notificationsQuery.hasNextPage ? (
            <div className="notification-drawer__footer">
              <Button
                type="button"
                variant="outline"
                size="xs"
                disabled={notificationsQuery.isFetchingNextPage}
                onClick={() => notificationsQuery.fetchNextPage()}
              >
                {notificationsQuery.isFetchingNextPage
                  ? t('portal.notifications.loadingMore')
                  : t('portal.notifications.loadMore')}
              </Button>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      <Dialog open={confirmAllOpen} onOpenChange={setConfirmAllOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('portal.notifications.confirmAll.title')}</DialogTitle>
            <DialogDescription>
              {t('portal.notifications.confirmAll.description')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => setConfirmAllOpen(false)}
            >
              {t('portal.notifications.confirmAll.cancel')}
            </Button>
            <Button
              type="button"
              size="xs"
              disabled={markAllRead.isPending}
              onClick={() => markAllRead.mutate()}
            >
              {t('portal.notifications.confirmAll.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function NotificationSkeleton() {
  return (
    <div className="notification-skeleton" aria-hidden="true">
      {[0, 1, 2, 3].map((item) => (
        <div key={item} className="notification-skeleton__item">
          <div className="notification-skeleton__meta">
            <div className="notification-skeleton__source">
              <Skeleton className="notification-skeleton__icon" />
              <Skeleton className="notification-skeleton__source-label" />
            </div>
            <Skeleton className="notification-skeleton__time" />
          </div>
          <Skeleton className="notification-skeleton__title" />
          <Skeleton className="notification-skeleton__line" />
          <Skeleton className="notification-skeleton__line notification-skeleton__line--short" />
        </div>
      ))}
    </div>
  );
}

function NotificationState({ message, action }: { message: string; action?: React.ReactNode }) {
  return (
    <div className="notification-drawer__state">
      <FileText aria-hidden="true" />
      <p>{message}</p>
      {action}
    </div>
  );
}

function updateNotificationPages(
  current: InfiniteData<NotificationPage> | undefined,
  notificationId: string,
  readAt: string,
  remove: boolean,
): InfiniteData<NotificationPage> | undefined {
  if (!current) return current;
  return {
    ...current,
    pages: current.pages.map((page) => ({
      ...page,
      items: remove
        ? page.items.filter((item) => item.id !== notificationId)
        : page.items.map((item) =>
            item.id === notificationId ? { ...item, read_at: readAt } : item,
          ),
    })),
  };
}
